import React from 'react';

import {
  HeaderRenderProps,
  MediaLayoutRenderProps,
  MenuItem,
  MenuItemBuilders,
} from '@/3.0/library/_components/operation/myCreations/_components/TaskCard/types';
import UserStore from '@/store/User';
import { F_CLOUD_TASK_TYPE } from '@/utils/const';
import { t } from '@/utils/helper/intl';

import { MediaItem } from './useTaskList';

/**
 * 视频翻译任务的自定义 Header 内容渲染
 * 显示：标题 + 输出数量 + 语言标签
 * 注意：不包含 MoreMenu，菜单由外层 TaskCard 统一处理（通过 renderMoreMenu）
 */
const videoTranslateHeader = (props: HeaderRenderProps) => {
  const tags = Array.from(
    new Set(
      props.mediaItems.map(
        (item) => item.rawData.extendInfo?.targetLanguage || 'default',
      ),
    ),
  );

  return (
    <div className="flex items-center">
      <div className="text-[12px] text-[#111] font-[600] pr-[24px]">{props.title}</div>
      <span className="text-[12px] text-[#111111CC]">
        {t('8ba7c3a7bef9d56fa9f8f252503f9eef')}: {props.mediaItems.length}
      </span>
      <div className="w-[1px] h-[16px] bg-[#dcdcdc] mx-[8px]" />
      <div className="flex gap-[4px] items-center">
        {tags.map((item) => {
          return (
            <span
              key={item}
              className=" px-[7px] bg-[#636ef4] text-[10px] text-[#fff] rounded-[4px]"
            >
              {item}
            </span>
          );
        })}
      </div>
    </div>
  );
};

/**
 * 视频翻译任务的自定义 Media 布局渲染
 * 按语言分组显示媒体项
 */
const videoTranslateMediaLayout = ({
  items,
  renderItem,
  createdAt,
}: MediaLayoutRenderProps) => {
  // 按语言分组
  const groupedItems = items.reduce(
    (acc, item) => {
      const category = item.rawData.extendInfo?.targetLanguage || 'default';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    },
    {} as Record<string, MediaItem[]>,
  );

  return (
    <div className="flex flex-col overflow-hidden flex-wrap">
      {Object.entries(groupedItems).map(([category, categoryItems]) => (
        <div key={category}>
          {/* 语言类别标题 */}
          <div className="text-[12px] text-[#111111CC] pb-[4px]">
            {t('8ba7c3a7bef9d56fa9f8f252503f9eef')}({category})
            <span className="inline-block mx-[8px]">|</span>
            <span>{createdAt}</span>
          </div>
          {/* 该语言的媒体项 */}
          <div className="flex flex-wrap gap-[8px]">
            {categoryItems.map((item) => renderItem(item))}
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * 视频翻译任务的自定义菜单
 * 根据用户 VIP 状态显示对应的下载和删除选项
 */
const videoTranslateMoreMenu = (
  builders: MenuItemBuilders,
  headerProps: HeaderRenderProps,
): MenuItem[] => {
  const userIdentify = UserStore.getState().currentVip.key;
  const { isPending, hasError } = headerProps;

  if (isPending) {
    return [];
  }

  if (hasError) {
    return [builders.delete];
  }

  if (userIdentify === 'freeVip') {
    // 免费用户：带水印下载 + 无水印升级提示 + 删除
    return [
      builders.downloadWithWatermark,
      builders.downloadWithoutWatermark,
      builders.delete,
    ];
  }

  // 付费用户：无水印下载 + 删除
  return [builders.download, builders.delete];
};

const faceSwapperV1MoreMenu = (
  builders: MenuItemBuilders,
  headerProps: HeaderRenderProps,
): MenuItem[] => {
  const userIdentify = UserStore.getState().currentVip.key;
  const { isPending, hasError } = headerProps;

  if (isPending) {
    return [];
  }

  if (hasError) {
    return [builders.delete];
  }

  if (userIdentify === 'freeVip') {
    // 免费用户：带水印下载 + 无水印升级提示 + 删除
    return [
      builders.downloadWithWatermark,
      builders.downloadWithoutWatermark,
      builders.delete,
    ];
  }

  // 付费用户：无水印下载 + 删除
  return [builders.download, builders.delete];
};

const videoOrImageGenerateHeader = (props: HeaderRenderProps) => {
  const { title, modelName, createdAt } = props;

  return (
    <div className="flex items-center">
      <div className="text-[12px] text-[#111] font-[600] pr-[12px]">{title}</div>
      <div className="text-[12px] text-[#111]">{modelName}</div>
      <div className="w-[1px] h-[16px] bg-[#dcdcdc] mx-[8px]" />
      <div className="text-[12px] text-[#111]">{createdAt}</div>
    </div>
  );
};

const VideoRemovalHeader = (props: HeaderRenderProps) => {
  const { title, createdAt } = props;

  return (
    <div className="flex items-center">
      <div className="text-[12px] text-[#111] font-[600] pr-[12px]">{title}</div>
      <span className="text-[12px] text-[#111111CC]">
        {t('8ba7c3a7bef9d56fa9f8f252503f9eef')}: {props.mediaItems?.length}
      </span>
      <div className="w-[1px] h-[16px] bg-[#dcdcdc] mx-[8px]" />
      <div className="text-[12px] text-[#111]">{createdAt}</div>
    </div>
  );
};

const ImageEnhancerHeader = (props: HeaderRenderProps) => {
  const { title, createdAt } = props;
  const tags = [
    ...new Set(props.mediaItems.map((item) => item.rawData.extendInfo?.resolution || '')),
  ];

  return (
    <div>
      <div className="flex items-center">
        <div className="text-[12px] text-[#111] font-[600] pr-[12px]">{title}</div>
        <span className="text-[12px] text-[#111111CC]">
          {t('8ba7c3a7bef9d56fa9f8f252503f9eef')}: {props.mediaItems?.length}
        </span>
        <div className="w-[1px] h-[16px] bg-[#dcdcdc] mx-[8px]" />
        <div className="flex gap-[4px] items-center">
          {tags.map((item) => {
            return (
              <span
                key={item}
                className=" px-[7px] bg-[#636ef4] text-[10px] text-[#fff] rounded-[4px]"
              >
                {item}
              </span>
            );
          })}
        </div>
      </div>
      <div className="text-[12px] text-[#111]">{createdAt}</div>
    </div>
  );
};

const voiceCloneMoreMenu = (
  builders: MenuItemBuilders,
  headerProps: HeaderRenderProps,
): MenuItem[] => {
  const { isPending, hasError } = headerProps;

  if (isPending) {
    return [];
  }

  if (hasError) {
    return [builders.delete];
  }

  // 付费用户：无水印下载 + 删除
  return [builders.download, builders.rename, builders.delete];
};

/**
 * 爆款复刻任务的自定义 Header
 * 显示：产品名称 + 生成时间
 */
const viralRemakeHeader = (props: HeaderRenderProps) => {
  const { title, createdAt } = props;

  return (
    <div className="flex items-center">
      <div className="text-[12px] text-[#111] font-[600] pr-[12px]">{title}</div>
      <div className="w-[1px] h-[16px] bg-[#dcdcdc] mx-[8px]" />
      <div className="text-[12px] text-[#111]">{createdAt}</div>
    </div>
  );
};

/**
 * 渲染配置 - 按任务类型映射
 * 支持灵活组合：可以只自定义 header、只自定义 moreMenu，或全部自定义
 */
const RENDER_CONFIGS: Record<
  string,
  {
    header?: (props: HeaderRenderProps) => React.ReactNode;
    mediaLayout?: (props: MediaLayoutRenderProps) => React.ReactNode;
    moreMenu?: (builders: MenuItemBuilders, headerProps: HeaderRenderProps) => MenuItem[];
    promptLabel?: string;
  }
> = {
  // 视频翻译任务：自定义全部（header + mediaLayout + moreMenu）
  videoTranslate: {
    header: videoTranslateHeader,
    mediaLayout: videoTranslateMediaLayout,
    moreMenu: videoTranslateMoreMenu,
  },

  // 换脸任务：只自定义 moreMenu（header 和 mediaLayout 使用默认）
  [F_CLOUD_TASK_TYPE.F_FACE_SWAP]: {
    header: undefined,
    mediaLayout: undefined,
    moreMenu: faceSwapperV1MoreMenu,
  },

  [F_CLOUD_TASK_TYPE.F_VIDEO_GENERATE]: {
    header: videoOrImageGenerateHeader,
    mediaLayout: undefined,
    moreMenu: undefined,
  },

  [F_CLOUD_TASK_TYPE.F_IMAGE_GENERATE]: {
    header: videoOrImageGenerateHeader,
    mediaLayout: undefined,
    moreMenu: undefined,
  },
  [F_CLOUD_TASK_TYPE.F_VOICE_CLONE]: {
    header: undefined,
    mediaLayout: undefined,
    moreMenu: voiceCloneMoreMenu,
  },
  [F_CLOUD_TASK_TYPE.F_TEXT_TO_SPEECH]: {
    header: undefined,
    mediaLayout: undefined,
    moreMenu: voiceCloneMoreMenu,
  },
  [F_CLOUD_TASK_TYPE.F_IMAGE_TRANSLATE]: {
    header: videoTranslateHeader,
    mediaLayout: videoTranslateMediaLayout,
    moreMenu: videoTranslateMoreMenu,
  },
  [F_CLOUD_TASK_TYPE.F_VIDEO_REMOVAL]: {
    header: VideoRemovalHeader,
    moreMenu: videoTranslateMoreMenu,
  },
  [F_CLOUD_TASK_TYPE.F_IMAGE_ENHANCE]: {
    header: ImageEnhancerHeader,
    moreMenu: videoTranslateMoreMenu,
  },
  [F_CLOUD_TASK_TYPE.F_VIDEO_EDIT]: {
    header: videoOrImageGenerateHeader,
    mediaLayout: undefined,
    moreMenu: undefined,
  },
  [F_CLOUD_TASK_TYPE.F_VIRAL_REMAKE]: {
    header: viralRemakeHeader,
    mediaLayout: undefined,
    moreMenu: undefined,
    promptLabel: t('f.viralRemake.productName'),
  },

  // 默认配置：全部使用 TaskCard 的默认行为
  default: {
    header: undefined,
    mediaLayout: undefined,
    moreMenu: undefined,
  },
};

/**
 * 根据任务类型返回对应的渲染配置
 * @param cloudTaskType 任务类型
 * @returns 渲染配置对象（包含 header、mediaLayout 和 moreMenu）
 *
 * 使用示例：
 * - 视频翻译：自定义 header + mediaLayout + moreMenu
 * - 换脸任务：默认 header + 默认 mediaLayout + 自定义 moreMenu
 * - 其他任务：全部默认
 */
export const getTaskCardRender = (cloudTaskType: F_CLOUD_TASK_TYPE) => {
  // 视频翻译类型列表
  const videoTranslateTypes = [
    F_CLOUD_TASK_TYPE.F_BATCH_SUBTITLE_TRANS,
    F_CLOUD_TASK_TYPE.F_BATCH_AUDIO_TRANS,
    F_CLOUD_TASK_TYPE.F_BATCH_SUBTITLE_AUDIO_TRANS,
    F_CLOUD_TASK_TYPE.F_MULTI_SUBTITLE_TRANS,
    F_CLOUD_TASK_TYPE.F_MULTI_SUBTITLE_AUDIO_TRANS,
  ];

  // 匹配视频翻译类型
  if (videoTranslateTypes.includes(cloudTaskType)) {
    return RENDER_CONFIGS.videoTranslate;
  }

  // 直接匹配特定任务类型（如 F_FACE_SWAP）
  if (RENDER_CONFIGS[cloudTaskType]) {
    return RENDER_CONFIGS[cloudTaskType];
  }

  // 默认配置
  return RENDER_CONFIGS.default;
};
